import { useEffect, useState } from 'react';
import type {
  DensityPreset,
  PuzzleData,
  PuzzleEntry,
  TrainSeed,
} from '../types';
import { generatePuzzleAsync } from '../trainPuzzleService';
import { useGameSettings, useGameStore } from '../store';
import * as historyService from '../historyService';
import { playClick } from '../sounds';
import { formatTrainSeedLabel } from '../trainSeed';
import { Game } from './Game';
import { ParallaxBg } from './ParallaxBg';
import { Button, Muted, Title } from './ui';
import './TrainGameScreen.css';

interface PuzzleLabGameProps {
  size: number;
  seed: TrainSeed;
  density: DensityPreset;
  entry: PuzzleEntry;
  onBack: () => void;
  onRegenerate: () => void;
  onGridDirty?: (dirty: boolean) => void;
  resetTrigger?: number;
  onFreshEntryConsumed?: () => void;
  onShare?: (opts: { solution: boolean[][]; grid?: import('../types').CellState[][] }) => Promise<'shared' | 'copied' | 'failed'>;
  initialProgress?: import('../types').CellState[][];
}

type PuzzleLabGameState =
  | { status: 'loading' }
  | { status: 'ready'; puzzle: PuzzleData }
  | { status: 'error'; error: Error };

export function PuzzleLabGame({
  size,
  seed,
  density,
  entry,
  onBack,
  onRegenerate,
  onGridDirty,
  resetTrigger,
  onFreshEntryConsumed,
  onShare,
  initialProgress,
}: PuzzleLabGameProps) {
  const gameSettings = useGameSettings();
  const [state, setState] = useState<PuzzleLabGameState>({
    status: 'loading',
  });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });

    generatePuzzleAsync(size, seed, density)
      .then((puzzle) => {
        if (cancelled) return;
        setState({ status: 'ready', puzzle });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          error:
            error instanceof Error
              ? error
              : new Error('Puzzle generation failed.'),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [density, seed, size]);

  const seedLabel = formatTrainSeedLabel(seed);
  const storageKey = `train_${size}_${encodeURIComponent(seed)}_${density}`;
  const puzzleName = formatTrainSeedLabel(seed);

  if (state.status === 'loading') {
    return (
      <div className="train-screen train-generation-screen" aria-busy="true">
        <ParallaxBg className="game-bg" />
        <div className="train-generation-card" role="status" aria-live="polite">
          <span className="train-generation-spinner" aria-hidden="true" />
          <Title as="h2" className="train-generation-title">
            Generating puzzle...
          </Title>
          <Muted as="p" className="train-generation-copy">
            Proving uniqueness for {size}x{size} seed {seedLabel} before the
            board opens.
          </Muted>
        </div>
        <Button
          variant="ghost"
          size="wide-sm"
          className="train-generation-action"
          onClick={() => {
            playClick();
            onBack();
          }}
        >
          Back
        </Button>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="train-screen train-generation-screen">
        <ParallaxBg className="game-bg" />
        <div className="train-generation-card train-generation-card--error">
          <Title as="h2" className="train-generation-title">
            Couldn&apos;t prove uniqueness
          </Title>
          <Muted as="p" className="train-generation-copy">
            {state.error.message}
          </Muted>
          <Muted as="p" className="train-generation-copy">
            That seed was rejected instead of starting an unchecked puzzle.
          </Muted>
        </div>
        <div className="train-generation-actions">
          <Button
            variant="primary"
            size="wide-sm"
            onClick={() => {
              playClick();
              onRegenerate();
            }}
          >
            Try Another Seed
          </Button>
          <Button
            variant="ghost"
            size="wide-sm"
            onClick={() => {
              playClick();
              onBack();
            }}
          >
            Back
          </Button>
        </div>
      </div>
    );
  }

  const isView = entry === 'view';
  const viewElapsed = isView
    ? useGameStore.getState().completions[storageKey]?.time
    : undefined;

  const trainPuzzle = state.puzzle;

  return (
    <Game
      key={`${storageKey}:${entry}`}
      name={puzzleName}
      puzzle={trainPuzzle}
      storageKey={storageKey}
      resumeFromSave={entry === 'resume'}
      viewMode={isView}
      viewElapsed={viewElapsed}
      mode="train"
      {...gameSettings}
      onGridDirty={onGridDirty}
      resetTrigger={resetTrigger}
      onFreshEntryConsumed={onFreshEntryConsumed}
      onBack={onBack}
      onNext={onRegenerate}
      onShare={onShare}
      initialProgress={initialProgress}
      onWin={() => {
        historyService.recordTrainWin({
          size,
          seed,
          density,
          solution: trainPuzzle.solution,
          storageKey,
          settings: {
            strict: gameSettings.strict,
            hintCount: gameSettings.hintCount,
            validateCount: gameSettings.validateCount,
            autoMark: gameSettings.autoMark,
          },
        });
      }}
    />
  );
}
